import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import Bugsnag from "@bugsnag/js";
import { DEFAULT_NOTIFICATION_PAYLOAD, NOTIFICATION_VARIANTS } from "@/utils/applicationConstants";
import { ANALYTIC_EVENTS, sendAnalytics } from "@/utils/analytics";
import {
  LENDING_PROGRAM_DETAIL_SERVICE_TAG,
  LENDING_PROGRAM_REDUCER_PATH,
  LENDING_PROGRAM_RELATED_TAGS,
  LENDING_PROGRAM_SERVICE_TAG,
} from "@/features/LendingProgram/utils/lendingProgramServiceTags";
import { LIST_SERVICE_TAG } from "@/shared/store/service/commonServiceTags";
import { baseUrl, onPrepareHeaders } from "@/redux/services/helpers";
import { contactService } from "@/modules/contacts/store/services/contact-service";
import { loanService } from "@/redux/services/loanService";
import { showNotification } from "@/redux/actions/notification";
import { getQueryParams } from "@/utils/httpRequest";
import {
  clearLenderCriteriaParameterOnProcessingState,
  setLenderCriteriaParameterOnProcessingState,
} from "@/redux/reducers/lender_criteria_parameter";
import { requestCloseModal } from "@/redux/actions/modal";
import { LENDER_LOAN_BEST_MATCH_HEATMAP_SERVICE_TAG } from "@/modules/contacts/features/lender/utils/lender-service-tags";
import { lenderService } from "@/modules/contacts/features/lender/store/services/lender-service";

export const lendingProgramService = createApi({
  reducerPath: LENDING_PROGRAM_REDUCER_PATH,
  tagTypes: LENDING_PROGRAM_RELATED_TAGS,
  baseQuery: fetchBaseQuery({
    baseUrl,
    prepareHeaders: onPrepareHeaders,
  }),
  endpoints: builder => ({
    getLendingPrograms: builder.query({
      query: id => `lenders/${id}/lending-programs`,
      providesTags: result =>
        result
          ? [
              ...result.map(({ lendingProgramId }) => ({ type: LENDING_PROGRAM_SERVICE_TAG, id: lendingProgramId })),
              { type: LENDING_PROGRAM_SERVICE_TAG, id: LIST_SERVICE_TAG },
            ]
          : [{ type: LENDING_PROGRAM_SERVICE_TAG, id: LIST_SERVICE_TAG }],
    }),
    getLendingProgram: builder.query({
      query: ({ lenderId, lendingProgramId }) => `lenders/${lenderId}/lending-programs/${lendingProgramId}`,
      providesTags: ({ lendingProgramId }) => [{ type: LENDING_PROGRAM_SERVICE_TAG, id: lendingProgramId }],
    }),
    addLendingProgram: builder.mutation({
      query: ({ lenderId: id, ...body }) => ({
        url: `lenders/${id}/lending-programs`,
        method: "POST",
        body,
      }),
      invalidatesTags: LENDING_PROGRAM_RELATED_TAGS,
      async onQueryStarted(id, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;

          sendAnalytics(ANALYTIC_EVENTS.LENDING_PROGRAM_CREATED, data);

          dispatch(contactService.util.invalidateTags(["Contact"]));
          dispatch(loanService.util.invalidateTags(["LoanLendersHeatMap"]));

          dispatch(
            showNotification({
              ...DEFAULT_NOTIFICATION_PAYLOAD,
              message: {
                ...DEFAULT_NOTIFICATION_PAYLOAD.message,
                description: "Lending Program have been created!",
                options: {
                  variant: NOTIFICATION_VARIANTS.SUCCESS,
                },
              },
            }),
          );
          dispatch(requestCloseModal());
          dispatch(lenderService.util.invalidateTags([LENDER_LOAN_BEST_MATCH_HEATMAP_SERVICE_TAG]));
        } catch (err) {
          Bugsnag.notify(err);
        }
      },
    }),
    updateLendingProgram: builder.mutation({
      query: ({ lenderId: id, lendingProgramId, ...body }) => ({
        url: `lenders/${id}/lending-programs/${lendingProgramId}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: LENDING_PROGRAM_RELATED_TAGS,
      async onQueryStarted(id, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;

          sendAnalytics(ANALYTIC_EVENTS.LENDING_PROGRAM_UPDATED, data);

          dispatch(contactService.util.invalidateTags(["Contact"]));
          dispatch(loanService.util.invalidateTags(["LoanLendersHeatMap"]));
          dispatch(lenderService.util.invalidateTags([LENDER_LOAN_BEST_MATCH_HEATMAP_SERVICE_TAG]));

          dispatch(
            showNotification({
              ...DEFAULT_NOTIFICATION_PAYLOAD,
              message: {
                ...DEFAULT_NOTIFICATION_PAYLOAD.message,
                description: "Lending Program have been updated!",
                options: {
                  variant: NOTIFICATION_VARIANTS.SUCCESS,
                },
              },
            }),
          );
        } catch (err) {
          Bugsnag.notify(err);
        }
      },
    }),
    deleteLendingProgram: builder.mutation({
      query: ({ lenderId: id, lendingProgramId }) => ({
        url: `lenders/${id}/lending-programs/${lendingProgramId}`,
        method: "DELETE",
      }),
      invalidatesTags: [{ type: LENDING_PROGRAM_SERVICE_TAG, id: LIST_SERVICE_TAG }],
      async onQueryStarted(id, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;

          sendAnalytics(ANALYTIC_EVENTS.LENDING_PROGRAM_DELETED, data);
          dispatch(
            showNotification({
              ...DEFAULT_NOTIFICATION_PAYLOAD,
              message: {
                ...DEFAULT_NOTIFICATION_PAYLOAD.message,
                description: "Lending Program have been deleted!",
                options: {
                  variant: NOTIFICATION_VARIANTS.SUCCESS,
                },
              },
            }),
          );

          dispatch(loanService.util.invalidateTags(["LoanLendersHeatMap"]));
          dispatch(lenderService.util.invalidateTags([LENDER_LOAN_BEST_MATCH_HEATMAP_SERVICE_TAG]));
          dispatch(requestCloseModal());
        } catch (err) {
          Bugsnag.notify(err);
        }
      },
    }),
    getLendingProgramDetails: builder.query({
      query: ({ lenderId, lendingProgramId, parameterType, ...params }) =>
        `lenders/${lenderId}/lending-programs/${lendingProgramId}/types/${parameterType}/parameters?${getQueryParams(
          params,
        )}`,
      providesTags: (result, resultType, { parameterType }) => [
        { type: LENDING_PROGRAM_DETAIL_SERVICE_TAG, id: parameterType },
      ],
    }),
    addLendingProgramDetail: builder.mutation({
      query: ({ lenderId, lendingProgramId, parameterType, parameterId }) => ({
        url: `lenders/${lenderId}/lending-programs/${lendingProgramId}/types/${parameterType}/parameters/${parameterId}`,
        method: "POST",
      }),
      invalidatesTags: () => [
        // { type: LENDING_PROGRAM_DETAIL_SERVICE_TAG, id: parameterType },
        // { type: LENDING_PROGRAM_SERVICE_TAG, id: lendingProgramId },
        { type: LENDING_PROGRAM_SERVICE_TAG, id: LIST_SERVICE_TAG },
      ],
      async onQueryStarted(
        { lenderId, lendingProgramId, parameterType, parameterId },
        { dispatch, queryFulfilled, getState },
      ) {
        // Get all cached queries for this parameter type
        const state = getState();
        const queries = lendingProgramService.util.selectCachedArgsForQuery(state, "getLendingProgramDetails");

        // Find all matching queries and update them optimistically
        const patchResults = [];

        queries.forEach(queryArgs => {
          if (
            queryArgs.lenderId === lenderId &&
            queryArgs.lendingProgramId === lendingProgramId &&
            queryArgs.parameterType === parameterType
          ) {
            const patchResult = dispatch(
              lendingProgramService.util.updateQueryData("getLendingProgramDetails", queryArgs, draft => {
                const parameter = draft.find(p => p.parameterId === parameterId);
                if (parameter) {
                  parameter.isActive = true;
                }
              }),
            );
            patchResults.push(patchResult);
          }
        });

        try {
          dispatch(setLenderCriteriaParameterOnProcessingState({ parameterId }));

          const { data } = await queryFulfilled;

          sendAnalytics(ANALYTIC_EVENTS.LENDING_PROGRAM_CREATED, data);

          dispatch(contactService.util.invalidateTags(["Contact"]));
          dispatch(loanService.util.invalidateTags(["LoanLendersHeatMap"]));
          dispatch(lenderService.util.invalidateTags([LENDER_LOAN_BEST_MATCH_HEATMAP_SERVICE_TAG]));

          dispatch(
            showNotification({
              ...DEFAULT_NOTIFICATION_PAYLOAD,
              message: {
                ...DEFAULT_NOTIFICATION_PAYLOAD.message,
                description: `${data.parameterName} has been added to the program!`,
                options: {
                  variant: NOTIFICATION_VARIANTS.SUCCESS,
                },
              },
            }),
          );

          dispatch(clearLenderCriteriaParameterOnProcessingState({ parameterId }));
        } catch (err) {
          // Revert all optimistic updates on error
          patchResults.forEach(patchResult => patchResult.undo());

          dispatch(clearLenderCriteriaParameterOnProcessingState({ parameterId }));
          Bugsnag.notify(err);
        }
      },
    }),
    addLendingProgramDetails: builder.mutation({
      query: ({ lenderId, lendingProgramId, parameterType, parentId }) => ({
        url: `lenders/${lenderId}/lending-programs/${lendingProgramId}/types/${parameterType}${
          parentId ? `?parentId=${parentId}` : ""
        }`,
        method: "POST",
      }),
      invalidatesTags: (result, error, { lendingProgramId, parameterType }) => [
        { type: LENDING_PROGRAM_DETAIL_SERVICE_TAG, id: parameterType },
        { type: LENDING_PROGRAM_SERVICE_TAG, id: lendingProgramId },
        { type: LENDING_PROGRAM_SERVICE_TAG, id: LIST_SERVICE_TAG },
      ],
      async onQueryStarted(args, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;

          // sendAnalytics(ANALYTIC_EVENTS.LENDER_CRITERIA_PARAMETER_CREATED, data);

          dispatch(contactService.util.invalidateTags(["Contact"]));
          dispatch(loanService.util.invalidateTags(["LoanLendersHeatMap"]));
          dispatch(lenderService.util.invalidateTags([LENDER_LOAN_BEST_MATCH_HEATMAP_SERVICE_TAG]));

          dispatch(
            showNotification({
              ...DEFAULT_NOTIFICATION_PAYLOAD,
              message: {
                ...DEFAULT_NOTIFICATION_PAYLOAD.message,
                description: "All has been added to the program!",
                options: {
                  variant: NOTIFICATION_VARIANTS.SUCCESS,
                },
              },
            }),
          );
        } catch (err) {
          Bugsnag.notify(err);
        }
      },
    }),
    deleteLendingProgramDetail: builder.mutation({
      query: ({ lenderId, lendingProgramId, parameterType, parameterId }) => ({
        url: `lenders/${lenderId}/lending-programs/${lendingProgramId}/types/${parameterType}/parameters/${parameterId}`,
        method: "DELETE",
      }),
      invalidatesTags: () => [
        // { type: LENDING_PROGRAM_DETAIL_SERVICE_TAG, id: parameterType },
        // { type: LENDING_PROGRAM_SERVICE_TAG, id: lendingProgramId },
        { type: LENDING_PROGRAM_SERVICE_TAG, id: LIST_SERVICE_TAG },
      ],
      async onQueryStarted(
        { lenderId, lendingProgramId, parameterType, parameterId },
        { dispatch, queryFulfilled, getState },
      ) {
        // Get all cached queries for this parameter type
        const state = getState();
        const queries = lendingProgramService.util.selectCachedArgsForQuery(state, "getLendingProgramDetails");

        // Find all matching queries and update them optimistically
        const patchResults = [];

        queries.forEach(queryArgs => {
          if (
            queryArgs.lenderId === lenderId &&
            queryArgs.lendingProgramId === lendingProgramId &&
            queryArgs.parameterType === parameterType
          ) {
            const patchResult = dispatch(
              lendingProgramService.util.updateQueryData("getLendingProgramDetails", queryArgs, draft => {
                const parameter = draft.find(p => p.parameterId === parameterId);
                if (parameter) {
                  parameter.isActive = false;
                }
              }),
            );
            patchResults.push(patchResult);
          }
        });

        try {
          dispatch(setLenderCriteriaParameterOnProcessingState({ parameterId }));
          const { data } = await queryFulfilled;

          sendAnalytics(ANALYTIC_EVENTS.LENDING_PROGRAM_PARAMETER_REMOVED, data);

          dispatch(contactService.util.invalidateTags(["Contact"]));
          dispatch(loanService.util.invalidateTags(["LoanLendersHeatMap"]));
          dispatch(lenderService.util.invalidateTags([LENDER_LOAN_BEST_MATCH_HEATMAP_SERVICE_TAG]));

          dispatch(
            showNotification({
              ...DEFAULT_NOTIFICATION_PAYLOAD,
              message: {
                ...DEFAULT_NOTIFICATION_PAYLOAD.message,
                description: `${data.parameterName} has been removed from the program!`,
                options: {
                  variant: NOTIFICATION_VARIANTS.SUCCESS,
                },
              },
            }),
          );
          dispatch(clearLenderCriteriaParameterOnProcessingState({ parameterId }));
        } catch (err) {
          // Revert all optimistic updates on error
          patchResults.forEach(patchResult => patchResult.undo());

          dispatch(clearLenderCriteriaParameterOnProcessingState({ parameterId }));
          Bugsnag.notify(err);
        }
      },
    }),
    deleteLendingProgramDetails: builder.mutation({
      query: ({ lenderId, lendingProgramId, parameterType, parentId }) => ({
        url: `lenders/${lenderId}/lending-programs/${lendingProgramId}/types/${parameterType}${
          parentId ? `?parentId=${parentId}` : ""
        }`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, { lendingProgramId, parameterType }) => [
        { type: LENDING_PROGRAM_DETAIL_SERVICE_TAG, id: parameterType },
        { type: LENDING_PROGRAM_SERVICE_TAG, id: lendingProgramId },
        { type: LENDING_PROGRAM_SERVICE_TAG, id: LIST_SERVICE_TAG },
      ],
      async onQueryStarted(args, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;

          dispatch(contactService.util.invalidateTags(["Contact"]));
          dispatch(loanService.util.invalidateTags(["LoanLendersHeatMap"]));
          dispatch(lenderService.util.invalidateTags([LENDER_LOAN_BEST_MATCH_HEATMAP_SERVICE_TAG]));

          dispatch(
            showNotification({
              ...DEFAULT_NOTIFICATION_PAYLOAD,
              message: {
                ...DEFAULT_NOTIFICATION_PAYLOAD.message,
                description: "All parameters have been removed from the program!",
                options: {
                  variant: NOTIFICATION_VARIANTS.SUCCESS,
                },
              },
            }),
          );
        } catch (err) {
          Bugsnag.notify(err);
        }
      },
    }),
  }),
});

export const {
  useGetLendingProgramsQuery,
  useGetLendingProgramQuery,
  useAddLendingProgramMutation,
  useUpdateLendingProgramMutation,
  useDeleteLendingProgramMutation,
  useGetLendingProgramDetailsQuery,
  useAddLendingProgramDetailMutation,
  useAddLendingProgramDetailsMutation,
  useDeleteLendingProgramDetailMutation,
  useDeleteLendingProgramDetailsMutation,
} = lendingProgramService;
